import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getColor } from "../../utils/colorfunctions";
import { formatWeightFromGrams } from "../../utils/weightUtils";
import { mixDisplayName } from "../../utils/mixDisplayName";
import ImageViewerModal from "../ImageViewerModal/ImageViewerModal";
import ShowroomProductModal from "../ShowroomProductModal/ShowroomProductModal";
import "./showroomItem.css";

const PRICE_UNIT_LABELS = { g: "/ g", oz: "/ oz", lb: "/ lb", unit: "/ unit", mg_thc: "/ mg THC" };
const formatPriceUnit = (unit) => PRICE_UNIT_LABELS[unit] || "";

const ShowroomItem = ({ item }) => {
    const [imageArray, setImageArray] = useState([]);
    const [showGallery, setShowGallery] = useState(false);
    const [showProductModal, setShowProductModal] = useState(false);

    useEffect(() => {
        const mainPicture = item.main_picture || "";
        const additionalPictures = item.additional_pictures
            ? item.additional_pictures
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
            : [];
        const coaPictures = item.coa_pictures
            ? item.coa_pictures
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
            : [];
        const seen = new Set();
        const newImageArray = [mainPicture, ...additionalPictures, ...coaPictures].filter((url) => {
            if (!url || seen.has(url)) return false;
            seen.add(url);
            return true;
        });
        setImageArray(newImageArray);
    }, [item]);

    const thca = parseFloat(item.THCa) || 0;
    const delta9 = parseFloat(item.delta_9_THC) || 0;
    const totalTHC = (thca * 0.877 + delta9).toFixed(2);
    const totalTerpenes = parseFloat(item.total_terpenes || 0).toFixed(2);

    const quantityDisplay = item.product_weight_unit === "units"
        ? (item.product_quantity != null ? `${item.product_quantity} units` : "—")
        : formatWeightFromGrams(item.product_quantity);

    const galleryPictures = imageArray.map((url, i) => ({ id: i, picture_url: url }));

    return (
        <div className="src-card fade-in">
            {/* HEADER */}
            <div className="src-card__header">
                <div className="src-card__header-row">
                    <span className="src-card__company">
                        {item.color && item.color !== "no color" && ((item.product_type || "").toLowerCase() === "flower" || (item.product_type || "").toLowerCase() === "pre-roll") && (
                            <span className="src-card__color-dot" style={{ backgroundColor: getColor(item.color) }} />
                        )}
                        {item.company}
                    </span>
                    {item.product_type && (
                        <span className="src-card__type-badge">
                            {item.product_type.charAt(0).toUpperCase() + item.product_type.slice(1)}
                        </span>
                    )}
                </div>
                {item.location_name && (
                    <span className="src-card__location-badge">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                        </svg>
                        {item.location_name}
                    </span>
                )}
                <p className="src-card__product-name">{mixDisplayName(item.product_name, item.product_type, item.created_at)}</p>
            </div>

            {/* BODY */}
            <div className="src-card__body">
                <div className="src-card__stats">
                    <div className="src-card__stat">
                        <span className="src-card__stat-label">Total THC</span>
                        <span className="src-card__stat-value">{totalTHC}%</span>
                        <span className="src-card__stat-sub">THCa×0.877 + Δ9</span>
                    </div>
                    <div className="src-card__stat-divider" />
                    <div className="src-card__stat">
                        <span className="src-card__stat-label">Total Terpenes</span>
                        <span className="src-card__stat-value">{totalTerpenes}%</span>
                    </div>
                </div>

                {imageArray.length > 0 && (
                    <button
                        className="src-card__images-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowGallery(true);
                        }}
                        type="button"
                    >
                        Images ({imageArray.length})
                    </button>
                )}
            </div>
  
            {/* FOOTER */}
            <div className="src-card__footer">
                <div className="src-card__footer-top">
                    <span className="src-card__quantity">{quantityDisplay}</span>
                    <div className="src-card__price-block">
                        <span className="src-card__price">${parseFloat(item.price || 0).toFixed(2)}</span>
                        {item.price_unit && ( 
                            <span className="src-card__price-unit"> {formatPriceUnit(item.price_unit)}</span>
                        )}
                    </div>
                </div>
                <button
                    className="src-card__cta"
                    onClick={() => setShowProductModal(true)}
                    type="button"
                >
                    View Product →
                </button>
            </div>
            {showGallery &&
                createPortal(
                    <ImageViewerModal
                        pictures={galleryPictures}
                        onClose={() => setShowGallery(false)}
                        canUpload={false}
                    />,
                    document.body
                )}
            {showProductModal &&
                createPortal(
                    <ShowroomProductModal
                        item={item}
                        onClose={() => setShowProductModal(false)}
                    />,
                    document.body
                )}
        </div>
    );
};

export default ShowroomItem;
